import React from "react";

// Splits on **bold**, e-mail addresses and URLs so they can be rendered as rich text.
const TOKEN =
  /(\*\*[^*]+\*\*|[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+|https?:\/\/[^\s)]+)/g;

const linkClass = "text-primary underline underline-offset-2 break-words";

const RichText = ({ text }) =>
  text
    .split(TOKEN)
    .filter(Boolean)
    .map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-semibold text-[#111111]">
            {part.slice(2, -2)}
          </strong>
        );
      }
      if (/^https?:\/\//.test(part)) {
        return (
          <a key={index} href={part} target="_blank" rel="noopener noreferrer" className={linkClass}>
            {part}
          </a>
        );
      }
      if (part.includes("@")) {
        return (
          <a key={index} href={`mailto:${part}`} className={linkClass}>
            {part}
          </a>
        );
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });

const bodyText = "text-[13px] leading-[22px] text-[#111111B8]";

const Block = ({ block }) => {
  switch (block.type) {
    case "p":
      return (
        <p className={bodyText}>
          <RichText text={block.text} />
        </p>
      );
    case "h":
      return (
        <h4 className="pt-1 text-[13px] font-semibold leading-[20px] text-[#111111]">{block.text}</h4>
      );
    case "h2":
      return (
        <h4 className="pt-2 text-[14px] font-semibold leading-[20px] text-primary">{block.text}</h4>
      );
    case "ul":
      return (
        <ul className={`list-disc space-y-1.5 pl-5 marker:text-primary ${bodyText}`}>
          {block.items.map((item, index) => (
            <li key={index}>
              <RichText text={item} />
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className={`list-decimal space-y-1.5 pl-5 marker:font-semibold marker:text-primary ${bodyText}`}>
          {block.items.map((item, index) => (
            <li key={index}>
              <RichText text={item} />
            </li>
          ))}
        </ol>
      );
    default:
      return null;
  }
};

/** Renders a privacy notice object (see constants/privacyNotice) as formatted, accessible content. */
const PrivacyNoticeContent = ({ notice }) => (
  <div lang={notice.lang} className="space-y-6">
    <div className="space-y-3">
      {notice.intro.map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </div>

    {notice.sections.map((section, index) => (
      <section key={section.id} aria-labelledby={`${section.id}-title`} className="space-y-3">
        <div className="flex items-start gap-2.5">
          <span
            aria-hidden="true"
            className="mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary"
          >
            {index + 1}
          </span>
          <h3 id={`${section.id}-title`} className="text-[15px] font-semibold leading-6 text-[#111111]">
            {section.title}
          </h3>
        </div>
        <div className="space-y-3">
          {section.blocks.map((block, blockIndex) => (
            <Block key={blockIndex} block={block} />
          ))}
        </div>
      </section>
    ))}

    <p className="border-t border-[#11111126] pt-4 text-[12px] text-[#1111117A]">
      <RichText text={notice.lastUpdated} />
    </p>
  </div>
);

export default PrivacyNoticeContent;
